<template>
  <el-row :gutter="16">
    <el-col :span="12">
      <PDatePicker
        type="date"
        v-model="Data"
        :disabled="PluggyByRange"
        :error="!PluggyByRange && !!errors?.Data"
        placeholder="Selecione uma data"
        label="Data"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col :span="12" style="margin-top: 2px">
      <PSelect
        v-model="Conta"
        placeholder="Selecione uma conta"
        label="Conta"
        :options="empresasOpt"
        :disabled="true"
        filterable
        clearable
      />
    </el-col>
    <el-col :span="24" style="margin: 0.7rem 0">
      <PCheckbox
        label="Selecionar um período"
        v-model="PluggyByRange"
        :disabled="periodoForcado"
      />
    </el-col>
    <el-col :span="12" v-if="PluggyByRange" style="margin-bottom: 16px">
      <PDatePicker
        type="date"
        v-model="DataInicio"
        :error="PluggyByRange && !DataInicio"
        placeholder="Selecione uma data de início"
        label="Início"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col :span="12" v-if="PluggyByRange" style="margin-bottom: 16px">
      <PDatePicker
        type="date"
        v-model="DataFim"
        :error="PluggyByRange && !DataFim"
        placeholder="Selecione uma data de fim"
        label="Fim"
        style="width: 100%"
        clearable
      />
    </el-col>
  </el-row>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from "vue";
import { PCheckbox, PDatePicker, PSelect } from "@f360-dev/f360-prisma";
import { useForm } from "vee-validate";
import { diContainer } from "@/inversify.config";
import { DITypes } from "@/core/@types/types";
import { IToastNotification } from "@/core/composables/notification/types";
import { IFinancasFeatureGateway } from "@/features/financas/services/ports/FinancasFeatureGateway";
import { parseLocalDate } from "../../composables/chamarServicoRequest";
import {
  IFormularioPluggyIntegracaoSchema,
  formPluggyIntegracaoSchema
} from "./formularioPluggySchema";
import { TWebservice } from "../../../../services/PainelDeIntegracaoesGateway";
import { TAPIRules } from "../../../../../../@types/IntegracoesAPIRules";

const props = defineProps<{
  webservice?: TWebservice;
  empresasOpt: { label: string; value: string }[];
  apiSelecionada?: TAPIRules;
}>();

const periodoForcado = computed(() => props.apiSelecionada?.forcesRange === true);

const financasFeatureGateway = diContainer.get<IFinancasFeatureGateway>(
  DITypes.FINANCAS_FEATURES
);

const formatDateInputValue = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// Regra do front-end legado: iniciar a consulta Pluggy sempre em D-1.
const getDefaultDateValue = (): string => {
  const yesterday = new Date();
  yesterday.setHours(0, 0, 0, 0);
  yesterday.setDate(yesterday.getDate() - 1);

  return formatDateInputValue(yesterday);
};

// Regra do front-end legado: permitir consultas somente a partir de D-365.
const getMinimumDateValue = (): string => {
  const minimumDate = new Date();
  minimumDate.setHours(0, 0, 0, 0);
  minimumDate.setDate(minimumDate.getDate() - 365);

  return formatDateInputValue(minimumDate);
};

const notification = diContainer.isBound(DITypes.NOTIFICATION)
  ? diContainer.get<IToastNotification<string>>(DITypes.NOTIFICATION)
  : undefined;
// Orienta o cliente a repetir a chamada quando a consulta falha.
const companyLookupErrorMessage =
  "Não foi possível obter a empresa da integração. Tente buscar novamente.";

const { handleSubmit, errors, setValues, values, defineField } =
  useForm<IFormularioPluggyIntegracaoSchema>({
    validationSchema: formPluggyIntegracaoSchema,
    initialValues: {
      Data: getDefaultDateValue(),
      Conta: "",
      PluggyByRange: periodoForcado.value,
      DataInicio: periodoForcado.value ? getDefaultDateValue() : null,
      DataFim: periodoForcado.value ? getDefaultDateValue() : null
    }
  });

const [Data] = defineField("Data");
const [Conta] = defineField("Conta");
const [PluggyByRange] = defineField("PluggyByRange");
const [DataInicio] = defineField("DataInicio");
const [DataFim] = defineField("DataFim");

const isOutsideLegacyDateRange = (value: unknown): boolean => {
  if (value == null || String(value).trim() === "") return false;

  const date = parseLocalDate(String(value));
  if (!date) return true;

  const minimumDate = parseLocalDate(getMinimumDateValue());
  const maximumDate = parseLocalDate(getDefaultDateValue());

  if (!minimumDate || !maximumDate) return true;

  return date < minimumDate || date > maximumDate;
};

// Regra do front-end legado: validar os limites do período e a ordem das datas.
const periodoUltrapassaLimiteDaApi = (): boolean => {
  if (
    isOutsideLegacyDateRange(Data.value) ||
    isOutsideLegacyDateRange(DataInicio.value) ||
    isOutsideLegacyDateRange(DataFim.value)
  ) {
    return true;
  }

  if (PluggyByRange.value) {
    const dataInicio =
      DataInicio.value != null
        ? parseLocalDate(String(DataInicio.value))
        : null;
    const dataFim =
      DataFim.value != null ? parseLocalDate(String(DataFim.value)) : null;

    if (dataInicio && dataFim && dataFim < dataInicio) return true;
  }

  return false;
};

const disabledSubmit = computed(() => {
  if (isOutsideLegacyDateRange(Data.value)) return true;

  const missingDate = !Data.value || String(Data.value).trim() === "";
  const missingEmpresa = !Conta.value || String(Conta.value).trim() === "";

  if (missingDate || missingEmpresa) return true;

  if (PluggyByRange.value) {
    const missingInicio =
      DataInicio.value == null || String(DataInicio.value).trim() === "";
    const missingFim =
      DataFim.value == null || String(DataFim.value).trim() === "";

    return missingInicio || missingFim || periodoUltrapassaLimiteDaApi();
  }

  return false;
});

const obterEmpresaPorConfiguracao = async () => {
  try {
    const response = await financasFeatureGateway.obterEmpresaPorConfiguracao(
      props.webservice?.id ?? props.webservice?._id ?? ""
    );

    if (!response?.Ok || !response.Result) {
      notification?.showNotification(
        response?.Error || companyLookupErrorMessage,
        {
          type: "error",
          timeout: 3000
        }
      );

      return;
    }

    setValues({
      Data: getDefaultDateValue(),
      Conta: response.Result ?? props.webservice?.empresa ?? "",
      PluggyByRange: periodoForcado.value,
      DataInicio: periodoForcado.value ? getDefaultDateValue() : null,
      DataFim: periodoForcado.value ? getDefaultDateValue() : null
    });
  } catch {
    notification?.showNotification(companyLookupErrorMessage, {
      type: "error",
      timeout: 3000
    });
  }
};

watch(PluggyByRange, (value) => {
  if (!value) return;

  const defaultDate = getDefaultDateValue();
  setValues({ Data: defaultDate, DataInicio: defaultDate, DataFim: defaultDate });
});

onMounted(async () => {
  try {
    if (props.apiSelecionada?.requiresCompanyLookup) {
      await obterEmpresaPorConfiguracao();

      return;
    }

    setValues({
      Data: getDefaultDateValue(),
      Conta: props.webservice?.empresa ?? "",
      PluggyByRange: periodoForcado.value,
      DataInicio: periodoForcado.value ? getDefaultDateValue() : null,
      DataFim: periodoForcado.value ? getDefaultDateValue() : null
    });
  } catch (error) {
    console.error(error);
  }
});

defineExpose({
  handleSubmit,
  errors,
  values,
  disabledSubmit,
  periodoForcado
});
</script>

<style lang="scss" scoped></style>
